import React, { useState, useRef, useEffect } from 'react';
import { useChatStore } from '../../hooks/useChatStore';
import { useAuthStore } from '../../hooks/useAuthStore';
import { absUrl } from '../../services/api';
import { format } from 'date-fns';
import { getAvatarEmoji } from './ChatWindow';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥', '🎉', '💯'];

// Рендер текста сообщения: ссылки кликабельны, @упоминания подсвечиваются.
function renderRichText(text) {
  if (!text) return null;
  const parts = text.split(/(https?:\/\/[^\s]+|(?<![\p{L}\p{N}])@[\p{L}\p{N}_.\-]+)/gu);
  return parts.map((part, i) => {
    if (/^https?:\/\//.test(part)) {
      const m = part.match(/^(https?:\/\/[^\s]*?)([.,;:!?)\]}>"'»«”“]+)$/);
      const url = m ? m[1] : part;
      const tail = m ? m[2] : '';
      return (
        <span key={i}>
          <a href={url} target="_blank" rel="noopener noreferrer" className="msg-link">
            {url}
          </a>
          {tail}
        </span>
      );
    }
    if (/^@[\p{L}\p{N}]/u.test(part)) {
      return (
        <span key={i} className="msg-mention">{part}</span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function CircularVideoMessage({ src, isWebm, knownRot }) {
  const [progress, setProgress] = useState(0);
  const CIRC = 2 * Math.PI * 47;
  return (
    <div className="msg-video-wrap">
      <video
        controls
        loop
        src={src}
        className="msg-video-circle"
        onLoadedMetadata={(e) => {
          const v = e.currentTarget;
          if (isWebm) {
            // webm не содержит rotation-метаданных: ротацию берём из имени файла
            // (video_msg_r90_...). knownRot == 0 — контент уже вертикальный.
            if (knownRot != null && knownRot > 0) {
              v.style.transform = `rotate(${knownRot}deg)`;
            } else if (knownRot == null) {
              // Старые файлы без ротации в имени: ландшафтные кадры поворачиваем на 90°.
              v.classList.toggle('msg-video-landscape', v.videoWidth > v.videoHeight);
            }
          } else if (knownRot == null || knownRot === 0) {
            // mp4: браузер сам применяет rotation-метаданные; эвристика нужна только
            // файлам без метаданных (старые записи приложения).
            v.classList.toggle('msg-video-landscape', v.videoWidth > v.videoHeight);
          }
        }}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (v.duration > 0) {
            setProgress(Math.min(v.currentTime / v.duration, 1));
          }
        }}
      />
      {progress > 0 && (
        <svg className="msg-video-ring" viewBox="0 0 100 100" aria-hidden="true">
          <circle className="msg-video-ring-track" cx="50" cy="50" r="47" fill="none" />
          <circle
            className="msg-video-ring-bar"
            cx="50" cy="50" r="47" fill="none"
            strokeDasharray={CIRC}
            strokeDashoffset={CIRC * (1 - progress)}
            strokeLinecap="round"
          />
        </svg>
      )}
    </div>
  );
}

export default function MessageBubble({ message, chatId, onUserClick, onForward, channelName, channelAvatar }) {
  const { editMessage, deleteMessage, addReaction, removeReaction, pinMessage, unpinMessage } = useChatStore();
  const { user } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.content);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [imgError, setImgError] = useState(false);
  const pickerRef = useRef(null);
  // В канале все сообщения отображаются от имени канала (не как "свои").
  const isOwn = channelName ? false : message.senderId === user?.id;
  const attachment = message.attachments?.[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowReactionPicker(false);
      }
    };
    if (showReactionPicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showReactionPicker]);

  const handleEdit = async () => {
    await editMessage(chatId, message.id, editText);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    await deleteMessage(chatId, message.id);
  };

  const handleReaction = async (emoji) => {
    const myReaction = (message.reactions || []).find(
      r => r.emoji === emoji && r.userId === user?.id
    );
    if (myReaction) {
      await removeReaction(chatId, message.id, emoji);
    } else {
      await addReaction(chatId, message.id, emoji);
    }
    setShowReactionPicker(false);
  };

  const senderAvatar = message.senderAvatarUrl ? absUrl(message.senderAvatarUrl) : null;

  const renderAvatar = () => {
    if (channelName) {
      return (
        <div className="msg-avatar" title={channelName}>
          {channelAvatar ? (
            <img src={channelAvatar} alt="" onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
          ) : (
            <span>📢</span>
          )}
        </div>
      );
    }
    if (!onUserClick || isOwn) return <div className="msg-avatar-spacer" />;
    return (
      <div className={`msg-avatar ${isOwn ? 'own' : ''}`}
        onClick={() => onUserClick && onUserClick()}
        title={message.senderDisplayName}>
        {senderAvatar ? (
          <img src={senderAvatar} alt="" onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
        ) : (
          <span>{getAvatarEmoji(message.senderDisplayName || message.senderUsername)}</span>
        )}
      </div>
    );
  };

  const messageBody = () => {
    if (message.isDeleted) {
      return <div className="msg-text" style={{ fontStyle: 'italic', color: '#999' }}>Сообщение удалено</div>;
    }
    switch (message.type) {
      case 'IMAGE': {
        const imgSrc = attachment?.fileUrl || message.content;
        if (imgSrc && !imgError) {
          const original = absUrl(imgSrc);
          const preview = original + (original.includes('?') ? '&' : '?') + 'size=400';
          return <img src={preview} alt="" className="msg-image" onClick={(e) => e.stopPropagation()}
            onError={() => setImgError(true)} />;
        }
        return <div className="msg-text">{message.content}</div>;
      }
      case 'STICKER': {
        const stickerSrc = attachment?.fileUrl || message.content;
        if (stickerSrc && !imgError) {
          return <img src={absUrl(stickerSrc)} alt="" className="msg-sticker" onError={() => setImgError(true)} />;
        }
        return <div className="msg-text">{message.content}</div>;
      }
      case 'FILE':
        return <div className="msg-file">📎 <a href={absUrl(attachment?.fileUrl || message.fileUrl)} target="_blank" rel="noreferrer">{attachment?.fileName || message.fileName || 'Файл'}</a></div>;
      case 'AUDIO':
        return <audio controls src={absUrl(attachment?.fileUrl || message.fileUrl)} style={{ maxWidth: '250px' }} />;
      case 'VIDEO':
        return <video controls src={absUrl(attachment?.fileUrl || message.fileUrl)} className="msg-video" style={{ maxWidth: '280px' }} />;
      case 'CALL_HISTORY': {
        const parts = (message.content || '').split('|');
        const st = parts[0] || 'completed';
        const ctype = parts[1] || 'audio';
        const dur = Math.floor((parseInt(parts[2] || '0', 10) || 0) / 1000);
        const dir = message.senderId === user?.id ? 'Исходящий' : 'Входящий';
        const typeLabel = ctype === 'video' ? 'видеозвонок' : 'звонок';
        const m = Math.floor(dur / 60).toString().padStart(2, '0');
        const sec = (dur % 60).toString().padStart(2, '0');
        const statusText = st === 'completed' ? ` · ${m}:${sec}` : st === 'declined' ? ' · отклонён' : ' · пропущен';
        return <div className="call-history-msg">{dir} {typeLabel}{statusText}</div>;
      }
      case 'AUDIO_MESSAGE':
        return <audio controls src={absUrl(attachment?.fileUrl || message.content)} style={{ maxWidth: '200px' }} />;
      case 'VIDEO_MESSAGE': {
        const vidSrc = attachment?.fileUrl || message.content;
        const rotMatch = vidSrc && vidSrc.match(/_r(\d+)_/);
        const knownRot = rotMatch ? parseInt(rotMatch[1], 10) : null;
        const isWebm = vidSrc && /\.webm$/i.test(vidSrc);
        return <CircularVideoMessage src={absUrl(vidSrc)} isWebm={isWebm} knownRot={knownRot} />;
      }
      default:
        return <div className="msg-text">{renderRichText(message.content)}</div>;
    }
  };

  const groupedReactions = {};
  (message.reactions || []).forEach(r => {
    if (!groupedReactions[r.emoji]) groupedReactions[r.emoji] = { emoji: r.emoji, count: 0, hasOwn: false };
    groupedReactions[r.emoji].count++;
    if (r.userId === user?.id) groupedReactions[r.emoji].hasOwn = true;
  });

  return (
    <div className={`message-row ${isOwn ? 'own' : 'other'}`}>
      {renderAvatar()}
      <div className={`message ${isOwn ? 'own' : 'other'}`} data-msg-id={message.id}>
        <div className="msg-sender">{channelName || message.senderDisplayName}</div>
        {messageBody()}
        {Object.keys(groupedReactions).length > 0 && (
          <div className="reactions-bar">
            {Object.values(groupedReactions).map(r => (
              <button
                key={r.emoji}
                className={`reaction-chip ${r.hasOwn ? 'active' : ''}`}
                onClick={() => handleReaction(r.emoji)}
              >
                {r.emoji} <span className="reaction-count">{r.count}</span>
              </button>
            ))}
          </div>
        )}
        <div className="message-meta">
          <span className="message-time">{message.createdAt ? format(new Date(message.createdAt), 'HH:mm') : ''}</span>
          {message.scheduledAt && <span className="scheduled-badge">⏰ отложено</span>}
          {message.isPinned && <span className="pinned-badge">📌</span>}
          {message.editedAt && <span className="edited-badge">ред.</span>}
        </div>
        <div className="message-actions">
          <button onClick={() => setShowReactionPicker(!showReactionPicker)}>😊</button>
          <button onClick={() => onForward && onForward(message)} title="Переслать">➡</button>
          {attachment && ['IMAGE', 'VIDEO', 'VIDEO_MESSAGE', 'AUDIO', 'AUDIO_MESSAGE', 'FILE'].includes(message.type) && (
            <a href={absUrl(attachment.fileUrl)} download={attachment.fileName || 'file'} title="Скачать">⬇</a>
          )}
          <button onClick={() => (message.isPinned ? unpinMessage(chatId, message.id) : pinMessage(chatId, message.id))}
            title={message.isPinned ? 'Открепить' : 'Закрепить'}>
            {message.isPinned ? '📌' : '📍'}
          </button>
          {/* Редактирование/удаление доступно автору сообщения (в канале — админу),
              независимо от стиля отображения */}
          {message.senderId === user?.id && !isEditing && (
            <>
              <button onClick={() => { setIsEditing(true); setEditText(message.content); }}>✎</button>
              <button onClick={handleDelete}>✕</button>
            </>
          )}
        </div>
        {showReactionPicker && (
          <div className="reaction-picker" ref={pickerRef}>
            {QUICK_REACTIONS.map(e => (
              <button key={e} className="reaction-picker-btn" onClick={() => handleReaction(e)}>{e}</button>
            ))}
          </div>
        )}
        {isEditing && (
          <div className="edit-input">
            <input value={editText} onChange={(e) => setEditText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleEdit(); }} />
            <button onClick={handleEdit}>✓</button>
            <button onClick={() => setIsEditing(false)}>✕</button>
          </div>
        )}
      </div>
    </div>
  );
}
